import { Button, Stack, Typography, useTheme } from "@mui/material";
import { ChevronDown, ChevronUp, Clock, Percent } from "lucide-react";
import type { FC } from "react";
import { LoanCalculatorSliderRow } from "web_myplann/Goals/Components/LoanCalculator/LoanCalculatorSliderRow";
import { LoanCalculatorSummary } from "web_myplann/Goals/Components/LoanCalculator/LoanCalculatorSummary";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import {
	formatRate,
	RATE_MAX,
	RATE_MIN,
	RATE_STEP,
	useLoanCalculator,
} from "web_myplann/Goals/Hooks/useLoanCalculator";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

type Props = {
	goal: GoalDetail;
};

export const GoalDetailLoanCalculator: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const {
		isMortgage,
		rate,
		setRate,
		termYears,
		setTermYears,
		termMin,
		termMax,
		collapsed,
		toggleCollapsed,
		monthlyPayment,
		totalPaid,
		overpay,
	} = useLoanCalculator(goal);

	if (goal.completed || goal.loanAmount <= 0) return null;

	const title = t(
		isMortgage
			? Resources.Goals.Detail.LoanCalculator.titleMortgage
			: Resources.Goals.Detail.LoanCalculator.titleLoan,
		{ amount: formatAmount(goal.loanAmount) },
	);
	const description = t(
		isMortgage
			? Resources.Goals.Detail.LoanCalculator.descriptionMortgage
			: Resources.Goals.Detail.LoanCalculator.descriptionLoan,
	);
	const minTermLabel = t(Resources.Goals.Detail.LoanCalculator.termValue, {
		count: termMin,
	});
	const maxTermLabel = t(Resources.Goals.Detail.LoanCalculator.termValue, {
		count: termMax,
	});

	return (
		<CollapsibleSection title={title}>
			<Typography
				variant="caption"
				component="p"
				color={theme.palette.grey[500]}
				px={1.6}
				pb={1}
				sx={{ lineHeight: 1.5 }}
			>
				{description}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				{!collapsed && (
					<>
						<LoanCalculatorSliderRow
							icon={<Percent size={16} color={theme.palette.grey[400]} />}
							label={t(Resources.Goals.Detail.LoanCalculator.rateLabel)}
							valueLabel={t(Resources.Goals.Detail.LoanCalculator.rateValue, {
								rate: formatRate(rate),
							})}
							value={rate}
							onChange={setRate}
							min={RATE_MIN}
							max={RATE_MAX}
							step={RATE_STEP}
							minLabel={t(Resources.Goals.Detail.LoanCalculator.rateMin)}
							maxLabel={t(Resources.Goals.Detail.LoanCalculator.rateMax)}
						/>
						<LoanCalculatorSliderRow
							icon={<Clock size={16} color={theme.palette.grey[400]} />}
							label={t(Resources.Goals.Detail.LoanCalculator.termLabel)}
							valueLabel={t(Resources.Goals.Detail.LoanCalculator.termValue, {
								count: termYears,
							})}
							value={termYears}
							onChange={setTermYears}
							min={termMin}
							max={termMax}
							step={1}
							minLabel={minTermLabel}
							maxLabel={maxTermLabel}
						/>
					</>
				)}

				<LoanCalculatorSummary
					rate={rate}
					termYears={termYears}
					monthlyPayment={monthlyPayment}
					totalPaid={totalPaid}
					overpay={overpay}
				/>

				<Button
					variant="inlineAction"
					disableRipple
					onClick={toggleCollapsed}
					sx={{ borderTop: `1px solid ${theme.palette.grey[100]}` }}
				>
					{collapsed ? (
						<>
							<ChevronDown size={14} />
							{t(Resources.Goals.Detail.LoanCalculator.showDetail)}
						</>
					) : (
						<>
							<ChevronUp size={14} />
							{t(Resources.Goals.Detail.LoanCalculator.hideDetail)}
						</>
					)}
				</Button>
			</Stack>
		</CollapsibleSection>
	);
};
